import { h, Component } from 'preact';
import { observer } from 'mobx-preact';
import { Text } from 'preact-i18n';
import util from 'preact-util';

import { formatTripletexVoucherNo } from '../../lib/tripletexVoucherNo';

function formatNumber(num) {
    if (num == null) return '0.00';
    return Number(num).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
}

function formatLocalDate(d) {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}

function getDefaultDates(periodType) {
    const now = new Date();
    let startDate, endDate;

    if (periodType === 'daily') {
        const yesterday = new Date(now);
        yesterday.setDate(yesterday.getDate() - 1);
        startDate = formatLocalDate(yesterday);
        endDate = startDate;
    } else if (periodType === 'weekly') {
        const dayOfWeek = now.getDay() || 7;
        const lastSunday = new Date(now);
        lastSunday.setDate(now.getDate() - dayOfWeek);
        const lastMonday = new Date(lastSunday);
        lastMonday.setDate(lastSunday.getDate() - 6);
        startDate = formatLocalDate(lastMonday);
        endDate = formatLocalDate(lastSunday);
    } else if (periodType === 'monthly') {
        const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const lastMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0);
        startDate = formatLocalDate(lastMonth);
        endDate = formatLocalDate(lastMonthEnd);
    } else {
        startDate = formatLocalDate(now);
        endDate = startDate;
    }

    return { startDate, endDate };
}

@observer
class TripletexVoucherReporting extends Component {
    constructor(props) {
        super(props);

        const { stores } = props;
        const { userStore } = stores;
        const customer = userStore.customer || {};
        const periodType = customer.featureFlags?.tripletexVoucherPeriod || 'daily';
        const { startDate, endDate } = getDefaultDates(periodType);

        this.state = {
            periodType,
            startDate,
            endDate,
            excludeInvoice: true,
            excludeImported: false,
            expanded: {},
            postError: null,
            postSuccess: null,
        };
    }

    toggleRow = (saleId) => {
        this.setState(prevState => ({
            expanded: { ...prevState.expanded, [saleId]: !prevState.expanded[saleId] },
        }));
    }

    componentDidMount() {
        const { stores } = this.props;
        const { tripletexStore } = stores;
        tripletexStore.getVoucherHistory();
    }

    componentWillUnmount() {
        const { stores } = this.props;
        const { tripletexStore } = stores;
        tripletexStore.clearVoucherData();
    }

    handlePeriodTypeChange = (e) => {
        const periodType = e.target.value;
        const { startDate, endDate } = getDefaultDates(periodType);
        this.setState({ periodType, startDate, endDate, postError: null, postSuccess: null });
    }

    handleDateChange = (e) => {
        this.setState({ [e.target.name]: e.target.value, postError: null, postSuccess: null });
    }

    syncFiltersToSaleStore = async () => {
        const { stores, callback = () => {}, setQuickDateFilter } = this.props;
        const { saleStore } = stores;
        const { startDate, endDate } = this.state;

        saleStore.updateKeyValue('cutoffDate', startDate);
        saleStore.updateKeyValue('cutoffDateEnd', endDate);
        saleStore.updateKeyValue('limit', 1000);

        if (setQuickDateFilter) {
            const d = new Date(startDate);
            setQuickDateFilter(d.getFullYear(), d.getMonth() + 1);
        }

        await callback();
    }

    loadPreview = async () => {
        const { stores } = this.props;
        const { tripletexStore } = stores;
        const { startDate, endDate, excludeInvoice, excludeImported } = this.state;

        this.setState({ postError: null, postSuccess: null });

        await this.syncFiltersToSaleStore();

        const response = await tripletexStore.getVoucherPreview({ startDate, endDate, excludeInvoice, excludeImported });
        if (response.status !== 200) {
            this.setState({ postError: response.message || 'Failed to load preview.' });
        }
    }

    postVoucher = async () => {
        const { stores } = this.props;
        const { tripletexStore } = stores;
        const { startDate, endDate, periodType, excludeInvoice, excludeImported } = this.state;

        this.setState({ postError: null, postSuccess: null });

        const response = await tripletexStore.postVoucher({ startDate, endDate, periodType, excludeInvoice, excludeImported });
        if (response.status === 201) {
            const voucherNo = formatTripletexVoucherNo(response.data?.voucherNumber, response.data?.voucherId, '');
            const msg = voucherNo
                ? `${response.message} Voucher no: ${voucherNo}`
                : response.message;
            this.setState({ postSuccess: msg });
        } else {
            this.setState({ postError: response.message || 'Failed to post voucher.' });
        }
    }

    renderReason = (reason) => {
        const map = {
            noPayment: { cls: 'bg-danger', id: 'tripletex.reasonNoPayment', def: 'No payment registered' },
            paymentMethodNoAccount: { cls: 'bg-danger', id: 'tripletex.reasonPaymentMethodNoAccount', def: 'Payment method without account setup' },
            underpaid: { cls: 'bg-warning text-dark', id: 'tripletex.reasonUnderpaid', def: 'Paid less than the sale' },
            overpaid: { cls: 'bg-warning text-dark', id: 'tripletex.reasonOverpaid', def: 'Paid more than the sale' },
        };
        const r = map[reason] || { cls: 'bg-secondary', id: 'tripletex.reasonUnknown', def: 'Unknown' };
        return <span class={`badge ${r.cls}`}><Text id={r.id}>{r.def}</Text></span>;
    }

    // Expanded detail for one avvik row — shows the sale status, date, product lines and the
    // registered payments so staff can see exactly why revenue and payments differ.
    renderRowDetail = (row) => {
        const { stores, gotoSale } = this.props;
        const { saleStore, appState } = stores;
        const language = appState?.language || 'no';
        // Use the SAME status/payment labels, icons and colors as the "Alle besøk" list.
        const statusKey = saleStore.getSaleStatusById(row.status);
        const statusText = saleStore.getSaleStatusById(row.status, language) || `Status ${row.status}`;
        const statusIcon = saleStore.getSaleStatusIcon(statusKey);
        const statusClass = saleStore.getSaleStatusClass(statusKey) || 'secondary';
        const paymentIcon = saleStore.getPaymentMethodIcon(row.paymentMethod);
        const paymentText = saleStore.getPaymentMethodText(row.paymentMethod, language);
        return (
            <div class='py-2'>
                <div class='mb-2 d-flex flex-wrap gap-3 small align-items-center'>
                    <span>
                        <strong><Text id='tripletex.saleStatus'>Status</Text>:</strong>{' '}
                        <span class={`badge text-bg-${statusClass}`}>
                            {!!statusIcon && <i class={`${statusIcon} me-1`} />}{statusText}
                        </span>
                    </span>
                    <span>
                        <strong><Text id='tripletex.paymentMethod'>Payment method</Text>:</strong>{' '}
                        {row.paymentMethod ? (
                            <span>{!!paymentIcon && <i class={`${paymentIcon} me-1`} />}{paymentText}</span>
                        ) : (
                            <span class='text-body-secondary'>—</span>
                        )}
                    </span>
                    <span>
                        <strong><Text id='tripletex.date'>Date</Text>:</strong> {util.isoDate(row.saleDate, false)}
                    </span>
                    {row.isImported && (
                        <span class='badge text-bg-secondary'><Text id='tripletex.imported'>Imported</Text></span>
                    )}
                    {!!gotoSale && (
                        <button
                            type='button'
                            class='btn btn-sm btn-outline-primary ms-auto'
                            onClick={() => gotoSale(row.saleId)}
                        >
                            <i class='fa-solid fa-arrow-up-right-from-square me-1' />
                            <Text id='tripletex.gotoSale'>Go to sale</Text>
                        </button>
                    )}
                </div>
                <div class='row'>
                    <div class='col-md-7 mb-2'>
                        <div class='fw-bold small mb-1'><Text id='tripletex.productLines'>Product lines</Text></div>
                        <table class='table table-sm mb-0'>
                            <tbody>
                                {row.products.map((p, i) => (
                                    <tr key={`p-${row.saleId}-${i}`}>
                                        <td>{p.name}</td>
                                        <td class='text-end text-body-secondary'>{p.qty} ×</td>
                                        <td class='text-end'>{formatNumber(p.gross)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div class='col-md-5 mb-2'>
                        <div class='fw-bold small mb-1'><Text id='tripletex.registeredPayments'>Registered payments</Text></div>
                        {row.paymentList.length > 0 ? (
                            <table class='table table-sm mb-0'>
                                <tbody>
                                    {row.paymentList.map((p, i) => (
                                        <tr key={`pm-${row.saleId}-${i}`}>
                                            <td>{p.paymentMethod}</td>
                                            <td class='text-end'>{formatNumber(p.amount)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        ) : (
                            <div class='text-body-secondary small'>
                                <Text id='tripletex.noPaymentsRegistered'>No payments registered for this sale.</Text>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    render() {
        const { stores, onClose } = this.props;
        const { tripletexStore, saleStore, appState } = stores;
        const language = appState?.language || 'no';
        const {
            periodType, startDate, endDate, excludeInvoice, excludeImported, expanded, postError, postSuccess,
        } = this.state;

        const preview = tripletexStore.voucherPreview;
        const previewLoading = tripletexStore.voucherPreviewLoading;
        const postLoading = tripletexStore.voucherPostLoading;
        const history = tripletexStore.voucherHistory || [];
        const historyLoading = tripletexStore.voucherHistoryLoading;

        return (
            <div class='w-100'>
                {/* Header with close button */}
                <div class='d-flex justify-content-between align-items-center mb-3'>
                    <h5 class='mb-0'>
                        <i class='fa-duotone fa-file-invoice me-2' />
                        <Text id='tripletex.voucherReporting'>Voucher Reporting</Text>
                    </h5>
                    {onClose && (
                        <button class='btn btn-sm btn-outline-secondary' onClick={onClose}>
                            <i class='fa-solid fa-times me-1' />
                            <Text id='general.close'>Close</Text>
                        </button>
                    )}
                </div>

                {/* Period Selection */}
                <div class='card mb-3'>
                    <div class='card-body'>
                        <div class='row g-3 align-items-end'>
                            <div class='col-auto'>
                                <label class='form-label'><Text id='tripletex.periodType'>Period Type</Text></label>
                                <select class='form-select' value={periodType} onChange={this.handlePeriodTypeChange}>
                                    <option value='daily'><Text id='tripletex.periodDaily'>Daily (Dagsoppgjor)</Text></option>
                                    <option value='weekly'><Text id='tripletex.periodWeekly'>Weekly (Ukesoppgjor)</Text></option>
                                    <option value='monthly'><Text id='tripletex.periodMonthly'>Monthly (Manedsoppgjor)</Text></option>
                                </select>
                            </div>
                            <div class='col-auto'>
                                <label class='form-label'><Text id='tripletex.startDate'>Start Date</Text></label>
                                <input type='date' class='form-control' name='startDate' value={startDate} onChange={this.handleDateChange} />
                            </div>
                            <div class='col-auto'>
                                <label class='form-label'><Text id='tripletex.endDate'>End Date</Text></label>
                                <input type='date' class='form-control' name='endDate' value={endDate} onChange={this.handleDateChange} />
                            </div>
                            <div class='col-auto d-flex align-items-end'>
                                <div class='mb-2'>
                                    <div class='form-check'>
                                        <input
                                            class='form-check-input'
                                            type='checkbox'
                                            id='excludeInvoice'
                                            checked={excludeInvoice}
                                            onChange={(e) => this.setState({ excludeInvoice: e.target.checked })}
                                        />
                                        <label class='form-check-label' for='excludeInvoice'>
                                            <Text id='tripletex.excludeInvoice'>Exclude invoice sales</Text>
                                        </label>
                                    </div>
                                    <div class='form-check'>
                                        <input
                                            class='form-check-input'
                                            type='checkbox'
                                            id='excludeImported'
                                            checked={excludeImported}
                                            onChange={(e) => this.setState({ excludeImported: e.target.checked })}
                                        />
                                        <label class='form-check-label' for='excludeImported'>
                                            <Text id='tripletex.excludeImported'>Exclude imported sales</Text>
                                        </label>
                                    </div>
                                </div>
                            </div>
                            <div class='col-auto'>
                                <button
                                    class='btn btn-primary'
                                    onClick={this.loadPreview}
                                    disabled={previewLoading}
                                >
                                    {previewLoading ? (
                                        <span><i class='fa-solid fa-spinner fa-spin me-1' /> <Text id='tripletex.loading'>Loading...</Text></span>
                                    ) : (
                                        <span><i class='fa-solid fa-eye me-1' /> <Text id='tripletex.loadPreview'>Load Preview</Text></span>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Tripletex Availability Warning */}
                {preview && !preview.tripletexAvailable && (
                    <div class='alert alert-warning'>
                        <i class='fa-solid fa-exclamation-triangle me-2' />
                        {preview.tripletexError}
                    </div>
                )}

                {/* Error/Success Messages */}
                {postError && (
                    <div class='alert alert-danger'>{postError}</div>
                )}
                {postSuccess && (
                    <div class='alert alert-success'>{postSuccess}</div>
                )}

                {/* Preview */}
                {preview && (
                    <div class='card mb-3'>
                        <div class='card-header'>
                            <strong>
                                <Text id='tripletex.preview'>Preview</Text>
                                : {periodType === 'daily' ? `Dagsoppgjor ${startDate}` :
                                    periodType === 'weekly' ? `Ukesoppgjor ${startDate} - ${endDate}` :
                                    `Manedsoppgjor ${startDate.substring(0, 7)}`}
                            </strong>
                        </div>
                        <div class='card-body'>
                            {/* Sale Counts */}
                            <div class='row mb-3'>
                                <div class='col'>
                                    <span class='badge bg-success me-2'>{preview.salesIncluded}</span>
                                    <Text id='tripletex.salesIncluded'>Sales included</Text>
                                </div>
                                {preview.excludedByInvoice > 0 && (
                                    <div class='col'>
                                        <span class='badge bg-secondary me-2'>{preview.excludedByInvoice}</span>
                                        <Text id='tripletex.excludedByInvoice'>Excluded (already invoiced)</Text>
                                    </div>
                                )}
                                {preview.excludedByVoucher > 0 && (
                                    <div class='col'>
                                        <span class='badge bg-secondary me-2'>{preview.excludedByVoucher}</span>
                                        <Text id='tripletex.excludedByVoucher'>Excluded (already in voucher)</Text>
                                    </div>
                                )}
                                {preview.excludedByInvoiceFilter > 0 && (
                                    <div class='col'>
                                        <span class='badge bg-warning text-dark me-2'>{preview.excludedByInvoiceFilter}</span>
                                        <Text id='tripletex.excludedByInvoiceFilter'>Excluded (invoice sales)</Text>
                                    </div>
                                )}
                                {preview.excludedByImported > 0 && (
                                    <div class='col'>
                                        <span class='badge bg-secondary me-2'>{preview.excludedByImported}</span>
                                        <Text id='tripletex.excludedByImported'>Excluded (imported sales)</Text>
                                    </div>
                                )}
                            </div>

                            {/* Revenue Lines */}
                            <h6><Text id='tripletex.revenue'>Revenue (Credit)</Text></h6>
                            <table class='table table-sm table-striped mb-3'>
                                <thead>
                                    <tr>
                                        <th><Text id='tripletex.account'>Account</Text></th>
                                        <th><Text id='tripletex.description'>Description</Text></th>
                                        <th><Text id='tripletex.vat'>VAT</Text></th>
                                        <th class='text-end'><Text id='tripletex.amount'>Amount</Text></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(preview.revenueLines || []).map((line, idx) => (
                                        <tr key={`rev-${idx}`}>
                                            <td>{line.account}</td>
                                            <td>{line.description}</td>
                                            <td>{line.vatRate}%</td>
                                            <td class='text-end'>{formatNumber(line.amountGross)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>

                            {/* Payment Lines */}
                            <h6><Text id='tripletex.payments'>Payments (Debit)</Text></h6>
                            <table class='table table-sm table-striped mb-3'>
                                <thead>
                                    <tr>
                                        <th><Text id='tripletex.account'>Account</Text></th>
                                        <th><Text id='tripletex.description'>Description</Text></th>
                                        <th class='text-end'><Text id='tripletex.amount'>Amount</Text></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(preview.paymentLines || []).map((line, idx) => (
                                        <tr key={`pay-${idx}`}>
                                            <td>{line.account}</td>
                                            <td>{line.description}</td>
                                            <td class='text-end'>{formatNumber(line.amountGross)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>

                            {/* Summary */}
                            <h6><Text id='tripletex.summary'>Summary</Text></h6>
                            <table class='table table-sm'>
                                <tbody>
                                    <tr>
                                        <td><Text id='tripletex.totalRevenueExVat'>Total revenue (ex. VAT)</Text></td>
                                        <td class='text-end'>{formatNumber(preview.summary?.totalRevenueExVat)}</td>
                                    </tr>
                                    <tr>
                                        <td><Text id='tripletex.totalVat'>Total VAT</Text></td>
                                        <td class='text-end'>{formatNumber(preview.summary?.totalVat)}</td>
                                    </tr>
                                    <tr>
                                        <td><strong><Text id='tripletex.totalGross'>Total gross</Text></strong></td>
                                        <td class='text-end'><strong>{formatNumber(preview.summary?.totalGross)}</strong></td>
                                    </tr>
                                    <tr>
                                        <td><Text id='tripletex.totalPayments'>Total payments</Text></td>
                                        <td class='text-end'>{formatNumber(preview.summary?.totalPayments)}</td>
                                    </tr>
                                    {preview.summary?.imbalance !== 0 && (
                                        <tr>
                                            <td>
                                                <Text id='tripletex.imbalance'>Imbalance</Text>
                                                {preview.summary?.balanced ? (
                                                    <span class='badge bg-warning ms-2'><Text id='tripletex.rounding'>Rounding</Text></span>
                                                ) : (
                                                    <span class='badge bg-danger ms-2'><Text id='tripletex.notBalanced'>Not balanced</Text></span>
                                                )}
                                            </td>
                                            <td class='text-end'>{formatNumber(preview.summary?.imbalance)}</td>
                                        </tr>
                                    )}
                                    <tr class={preview.summary?.balanced ? 'table-success' : 'table-danger'}>
                                        <td>
                                            <strong><Text id='tripletex.balance'>Balance</Text></strong>
                                            {preview.summary?.balanced ? (
                                                <i class='fa-solid fa-check-circle text-success ms-2' />
                                            ) : (
                                                <i class='fa-solid fa-times-circle text-danger ms-2' />
                                            )}
                                        </td>
                                        <td class='text-end'>
                                            <strong>{preview.summary?.balanced ? '0.00' : formatNumber(preview.summary?.imbalance)}</strong>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>

                            {/* Per-sale breakdown of the imbalance — lets staff see which sales
                                cause the "Ubalanse" and why (TICKET-01095). */}
                            {preview.saleImbalances
                                && (preview.saleImbalances.rows.length > 0 || preview.saleImbalances.roundingCount > 0) && (
                                <div class='mt-4'>
                                    <h6>
                                        <i class='fa-duotone fa-magnifying-glass-dollar me-1' />
                                        <Text id='tripletex.saleImbalances'>Discrepancies per sale</Text>
                                    </h6>
                                    <p class='text-body-secondary small mb-2'>
                                        <Text id='tripletex.saleImbalancesHelp'>These sales have a difference between the sale amount and the registered payments. Together they make up the imbalance above.</Text>
                                    </p>
                                    {preview.saleImbalances.rows.length > 0 && (
                                        <table class='table table-sm table-striped mb-2'>
                                            <thead>
                                                <tr>
                                                    <th><Text id='tripletex.saleNo'>Sale no.</Text></th>
                                                    <th><Text id='tripletex.reason'>Likely cause</Text></th>
                                                    <th class='text-end'><Text id='tripletex.saleRevenue'>Sale (gross)</Text></th>
                                                    <th class='text-end'><Text id='tripletex.salePaid'>Paid</Text></th>
                                                    <th class='text-end'><Text id='tripletex.difference'>Difference</Text></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {preview.saleImbalances.rows.map((row) => {
                                                    const isOpen = !!expanded[row.saleId];
                                                    const statusText = saleStore.getSaleStatusById(row.status, language);
                                                    const statusClass = saleStore.getSaleStatusClass(
                                                        saleStore.getSaleStatusById(row.status),
                                                    ) || 'secondary';
                                                    const result = [
                                                        <tr
                                                            key={`imb-${row.saleId}`}
                                                            style='cursor: pointer;'
                                                            onClick={() => this.toggleRow(row.saleId)}
                                                        >
                                                            <td>
                                                                <i class={`fa-solid fa-chevron-${isOpen ? 'down' : 'right'} me-2 text-body-secondary`} />
                                                                {row.saleNumber}
                                                            </td>
                                                            <td>
                                                                {this.renderReason(row.reason)}
                                                                {row.status > 0 && !!statusText && (
                                                                    <span class={`badge text-bg-${statusClass} ms-1`}>{statusText}</span>
                                                                )}
                                                            </td>
                                                            <td class='text-end'>{formatNumber(row.revenue)}</td>
                                                            <td class='text-end'>{formatNumber(row.payments)}</td>
                                                            <td class='text-end fw-bold'>{formatNumber(row.diff)}</td>
                                                        </tr>,
                                                    ];
                                                    if (isOpen) {
                                                        result.push(
                                                            <tr key={`imb-det-${row.saleId}`} class='table-active'>
                                                                <td colspan='5'>{this.renderRowDetail(row)}</td>
                                                            </tr>,
                                                        );
                                                    }
                                                    return result;
                                                })}
                                            </tbody>
                                        </table>
                                    )}
                                    {preview.saleImbalances.roundingCount > 0 && (
                                        <div class='text-body-secondary small'>
                                            <i class='fa-solid fa-circle-info me-1' />
                                            <Text
                                                id='tripletex.roundingSalesInfo'
                                                fields={{
                                                    count: preview.saleImbalances.roundingCount,
                                                    sum: formatNumber(preview.saleImbalances.roundingTotal),
                                                }}
                                            >
                                                {`${preview.saleImbalances.roundingCount} sales have øre-rounding totalling ${formatNumber(preview.saleImbalances.roundingTotal)} kr — normal, posted as a single "Avrunding" line.`}
                                            </Text>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Post Button */}
                            <div class='mt-3'>
                                {/* Explain — right at the button — why it is disabled, so the
                                    user is not left staring at an unresponsive ("frozen") button. */}
                                {!postLoading && !preview.tripletexAvailable && (
                                    <div class='alert alert-warning d-flex align-items-start mb-2'>
                                        <i class='fa-solid fa-triangle-exclamation me-2 mt-1' />
                                        <div>
                                            <strong><Text id='tripletex.cannotPostTitle'>Cannot send to Tripletex yet</Text></strong>
                                            <div><Text id='tripletex.disabledNoAccess'>The Tripletex connection is missing access to Accounting/Vouchers (Regnskap/Bilag). Grant the API connection voucher access inside Tripletex.</Text></div>
                                            {preview.tripletexError && (
                                                <small class='text-body-secondary d-block mt-1'>{preview.tripletexError}</small>
                                            )}
                                        </div>
                                    </div>
                                )}
                                {!postLoading && preview.tripletexAvailable && preview.salesIncluded === 0 && (
                                    <div class='alert alert-info d-flex align-items-start mb-2'>
                                        <i class='fa-solid fa-circle-info me-2 mt-1' />
                                        <Text id='tripletex.disabledNoSales'>No sales to send for this period. They may already have been transferred to Tripletex (as individual invoices or an earlier voucher).</Text>
                                    </div>
                                )}
                                {!postLoading && preview.tripletexAvailable && preview.salesIncluded > 0 && !preview.summary?.balanced && (
                                    <div class='alert alert-danger d-flex align-items-start mb-2'>
                                        <i class='fa-solid fa-triangle-exclamation me-2 mt-1' />
                                        <Text id='tripletex.disabledNotBalanced'>The voucher does not balance, so it cannot be sent. Please review the sales for this period.</Text>
                                    </div>
                                )}
                                <div class='d-flex justify-content-end'>
                                    <button
                                        class='btn btn-success btn-lg'
                                        onClick={this.postVoucher}
                                        disabled={!preview.summary?.balanced || postLoading || preview.salesIncluded === 0 || !preview.tripletexAvailable}
                                    >
                                        {postLoading ? (
                                            <span><i class='fa-solid fa-spinner fa-spin me-1' /> <Text id='tripletex.posting'>Posting...</Text></span>
                                        ) : (
                                            <span><i class='fa-solid fa-paper-plane me-1' /> <Text id='tripletex.postToTripletex'>Post to Tripletex</Text></span>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Previous Vouchers */}
                <div class='card'>
                    <div class='card-header'>
                        <strong><Text id='tripletex.previousVouchers'>Previous Vouchers</Text></strong>
                    </div>
                    <div class='card-body'>
                        {historyLoading && (
                            <div class='text-center py-3'>
                                <i class='fa-solid fa-spinner fa-spin me-1' /> <Text id='tripletex.loading'>Loading...</Text>
                            </div>
                        )}
                        {!historyLoading && history.length === 0 && (
                            <p class='text-body-secondary'><Text id='tripletex.noVouchers'>No vouchers posted yet.</Text></p>
                        )}
                        {!historyLoading && history.length > 0 && (
                            <table class='table table-sm table-striped'>
                                <thead>
                                    <tr>
                                        <th><Text id='tripletex.date'>Date</Text></th>
                                        <th><Text id='tripletex.voucherNo'>Voucher No.</Text></th>
                                        <th><Text id='tripletex.period'>Period</Text></th>
                                        <th><Text id='tripletex.sales'>Sales</Text></th>
                                        <th class='text-end'><Text id='tripletex.amount'>Amount</Text></th>
                                        <th><Text id='tripletex.status'>Status</Text></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {history.map(v => (
                                        <tr key={v.id}>
                                            <td>{v.voucherDate}</td>
                                            <td>{formatTripletexVoucherNo(v.tripletexVoucherNumber, v.tripletexVoucherId)}</td>
                                            <td>
                                                {v.periodType === 'daily' && <Text id='tripletex.daily'>Daily</Text>}
                                                {v.periodType === 'weekly' && <Text id='tripletex.weekly'>Weekly</Text>}
                                                {v.periodType !== 'daily' && v.periodType !== 'weekly' && <Text id='tripletex.monthly'>Monthly</Text>}
                                            </td>
                                            <td>{(v.saleIds || []).length}</td>
                                            <td class='text-end'>{formatNumber(v.summary?.totalGross)}</td>
                                            <td>
                                                {v.status === 'posted' ? (
                                                    <span class='badge bg-success'><Text id='tripletex.statusPosted'>Posted</Text></span>
                                                ) : (
                                                    <span class='badge bg-danger'><Text id='tripletex.statusError'>Error</Text></span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            </div>
        );
    }
}

export default TripletexVoucherReporting;
